<template>
  <div class="mx-auto mb-4 flex w-full flex-nowrap gap-x-2">
    <div v-if="!simple" class="flex flex-col justify-center gap-y-2">
      <tiny-user-thumb v-for="author in authors" :key="author" :uuid="author" />
    </div>
    <information-text
      v-if="quoteData.reported_by && quoteData.reported_by.length > 0"
      color="yellow"
    >
      <p class="text-center font-medium">
        {{ $t("components.report.hasBeenReportedBy") }}
        {{ reporterNames }}.
        {{ $t("components.report.adminsWillDetermine") }}
      </p>
    </information-text>
    <div
      v-else
      class="relative flex w-full flex-col justify-center overflow-hidden rounded-lg border-2 border-black-dark bg-white p-4 py-4 shadow-red-hard dark:bg-kekkle-dark-light md:p-8"
    >
      <pencil-alt-icon
        v-if="canEdit && !simple"
        class="absolute left-2 top-2 w-6 transform duration-300 hover:-translate-y-1 dark:text-slate-200"
        @click="goToEditView"
      />
      <dots-vertical-icon
        class="absolute right-2 top-2 w-6 align-bottom text-gray-600 duration-300 hover:-translate-y-1 dark:text-slate-200"
        @click="reportModalIsOpen = !reportModalIsOpen"
      />

      <div>
        <div class="px-2 text-center italic dark:text-slate-200">
          {{ formattedTimeAgo }}
        </div>
        <div
          class="px-2 pb-2 text-center text-xs italic text-gray-500 dark:text-slate-400"
        >
          {{ formattedDateAgo }}
        </div>

        <h2
          translate="no"
          class="mb-2 mt-4 break-words text-center text-xl font-semibold dark:text-slate-200"
        >
          {{ quote }}
        </h2>
        <h3
          v-if="context"
          class="my-2 cursor-pointer text-center italic text-gray-800 dark:text-slate-200"
          @click.stop.prevent="toggleContext"
        >
          {{ contextVisible ? context : $t("components.quote.context") }}
        </h3>
        <p
          v-for="(name, index) in authorNames"
          :key="index"
          class="text-right dark:text-slate-200"
        >
          {{ index === userAuthUid ? $t("components.quote.you") : ""
          }}{{ name }}
        </p>
        <p v-if="randomAuthor" class="text-right dark:text-slate-200">
          {{ randomAuthor }}
        </p>
      </div>
    </div>
    <div
      v-if="!simple"
      class="flex flex-col justify-center"
      @click="goToComments"
    >
      <chat-alt2-icon
        class="w-6 transform duration-300 hover:-translate-y-2 dark:text-slate-200"
      />
      <p class="text-center text-lg font-medium dark:text-slate-200">
        {{ numberOfComments }}
      </p>
    </div>
  </div>

  <teleport to="body">
    <conformation-modal
      safe-mode
      :visible="reportModalIsOpen"
      :confirm-text="$t('components.report.report')"
      :cancel-text="$t('components.report.close')"
      :loading="reporting"
      @cancel="reportModalIsOpen = false"
      @confirm="handleReportQuote"
    >
      <h3 class="text-xl font-bold dark:text-slate-200">
        {{ $t("components.report.reportTitle") }}
      </h3>
      <p class="my-2">
        {{ $t("components.report.reportText") }}
      </p>
      <information-text
        class="my-2"
        color="orange"
        :information="$t('components.report.reportInformation')"
      />
    </conformation-modal>
  </teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ChatAlt2Icon } from "@heroicons/vue/outline";
import { PencilAltIcon, DotsVerticalIcon } from "@heroicons/vue/outline";
import { logEvent } from "firebase/analytics";
import type { QuoteDocument } from "@kekkle/shared/types/documents/quote-document/quote-document";
import TinyUserThumb from "./TinyUserThumb.vue";
import ConformationModal from "@/components/Modals/ConformationModal.vue";
import InformationText from "@/components/InformationText.vue";
import { firebaseAnalytics } from "@/helpers/firebase/firebase";
import { timeAgo } from "@/helpers/dates/time-ago";
import { dateAgo } from "@/helpers/dates/date-ago";
import { setErrorToast, setSuccessToast } from "@/helpers/notifications/toast";
import {
  formatMemberNamesMap,
  formatMemberNamesList,
} from "@/helpers/utils/format-member-names";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import { useQuotesStore } from "@/stores/modules/quotes/quotes";
import { useQuoteManagement } from "@/composables/useQuoteManagement";

// Extended type to include fields that may be added at runtime
type QuoteWithMetadata = QuoteDocument & {
  reported_by?: string[] | null;
  number_of_comments?: number;
};

type Props = {
  simple?: boolean;
  quoteData: QuoteWithMetadata;
  quoteId: string;
};

type Emits = {
  (e: "goToComments", payload: { quoteId: string; groupId: string }): void;
};

const props = withDefaults(defineProps<Props>(), {
  simple: false,
});

const emit = defineEmits<Emits>();
const { t } = useI18n();

const authStore = useAuthStore();
const groupsStore = useGroupsStore();
const userStore = useUserStore();
const quotesStore = useQuotesStore();
const { openEditQuoteBottomSheet } = useQuoteManagement();

// State
const contextVisible = ref(false);
const reportModalIsOpen = ref(false);
const reporting = ref(false);

watch(
  () => props.quoteId,
  () => {
    contextVisible.value = false;
  },
);

const quote = computed(() => {
  return props.quoteData.quote ?? "";
});

const context = computed(() => {
  return props.quoteData.context ?? "";
});

const authors = computed(() => {
  return props.quoteData.authors ?? [];
});

const numberOfComments = computed(() => {
  return props.quoteData.number_of_comments ?? 0;
});

const date = computed(() => {
  return props.quoteData.timestamp ?? null;
});

const userAuthUid = computed(() => {
  return authStore.authUuid;
});

const groupData = computed(() => {
  return groupsStore.groupData;
});

const reporterNames = computed(() => {
  if (!groupData.value?.members) {
    return "";
  }
  return formatMemberNamesList(
    props.quoteData.reported_by,
    groupData.value.members,
    t("global.deleted_user"),
  );
});

const authorNames = computed(() => {
  if (!groupData.value?.members) {
    return {};
  }
  return formatMemberNamesMap(
    props.quoteData.authors,
    groupData.value.members,
    t("global.deleted_user"),
  );
});

const randomAuthor = computed(() => {
  return props.quoteData.custom_author ?? "";
});

const canEdit = computed(() => {
  return groupsStore.isAdmin || groupsStore.isOrganiser;
});

const formattedTimeAgo = computed(() => {
  return date.value ? timeAgo(date.value) : "";
});

const formattedDateAgo = computed(() => {
  return date.value ? dateAgo(date.value) : "";
});

// Methods
function toggleContext() {
  contextVisible.value = !contextVisible.value;
  logEvent(firebaseAnalytics, "quote_interaction", {
    name: "toggle_context",
    visible: contextVisible.value,
  });
}

function goToComments() {
  if (!userStore.activeGroup) return;

  emit("goToComments", {
    quoteId: props.quoteId,
    groupId: userStore.activeGroup,
  });
}

async function handleReportQuote() {
  if (!userStore.activeGroup || !authStore.authUuid) {
    setErrorToast({
      message: t("components.report.toastNotReported"),
    });
    return;
  }

  try {
    reporting.value = true;

    // Report the quote via the store (handles Firestore update and refetch)
    await quotesStore.reportQuote(props.quoteId);

    setSuccessToast({
      message: t("components.report.toastReported"),
    });
  } catch (error) {
    console.error("Failed to report quote:", error);
    setErrorToast({
      message: t("components.report.toastNotReported"),
    });
  } finally {
    reportModalIsOpen.value = false;
    reporting.value = false;
  }
}

function goToEditView() {
  openEditQuoteBottomSheet(props.quoteData);
}
</script>
